import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, Fragment as _Fragment, createBlock as _createBlock, renderList as _renderList } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-xs ml-1"
}
const _hoisted_2 = { class: "text-body-2 font-weight-bold" }
const _hoisted_3 = { class: "flex justify-between py-1" }
const _hoisted_4 = { class: "text-caption text-grey-darken-1" }
const _hoisted_5 = {
  key: 0,
  class: "flex justify-between py-1 px-1 bg-grey-lighten-4 rounded"
}
const _hoisted_6 = { class: "text-body-2 font-weight-bold" }
const _hoisted_7 = {
  key: 0,
  class: "text-xs ml-1"
}
const _hoisted_8 = { class: "flex justify-between py-1" }
const _hoisted_9 = { class: "flex justify-between py-1" }
const _hoisted_10 = { class: "text-caption text-grey-darken-1" }
const _hoisted_11 = { class: "flex justify-between py-1" }
const _hoisted_12 = {
  key: 0,
  class: "text-xs ml-1"
}
const _hoisted_13 = {
  key: 0,
  class: "flex justify-between py-1"
}
const _hoisted_14 = { class: "text-caption text-grey-darken-1" }
const _hoisted_15 = { class: "text-body-2 font-weight-bold" }
const _hoisted_16 = {
  key: 2,
  class: "flex justify-between py-1 px-1 bg-grey-lighten-4 rounded"
}
const _hoisted_17 = { class: "text-body-2 font-weight-bold" }
const _hoisted_18 = {
  key: 0,
  class: "text-xs ml-1"
}
const _hoisted_19 = {
  key: 0,
  class: "text-xs ml-1"
}
const _hoisted_20 = {
  key: 0,
  class: "text-xs ml-1"
}
const _hoisted_21 = {
  key: 0,
  class: "flex justify-between py-1"
}
const _hoisted_22 = { class: "text-caption text-grey-darken-1" }
const _hoisted_23 = { class: "text-body-2 font-weight-bold" }
const _hoisted_24 = { class: "text-body-2 font-weight-bold" }
const _hoisted_25 = { class: "text-body-2 font-weight-bold" }
const _hoisted_26 = {
  key: 0,
  class: "text-xs ml-1"
}
const _hoisted_27 = { class: "flex justify-between py-1" }
const _hoisted_28 = { class: "flex items-center gap-1" }
const _hoisted_29 = {
  key: 6,
  class: "text-body-2 text-center"
}
const _hoisted_30 = { class: "text-body-2 font-weight-medium text-green-darken-2 mt-1" }
const _hoisted_31 = {
  key: 1,
  class: "text-body-2 text-grey text-center py-4"
}

import { computed } from 'vue';
import { formatCurrency, formatLongDateString } from '@/services/utils';
import LlTooltip from '@/components/LlTooltip.vue';
import LlIcon from '@/components/LlIcon.vue';
import type { ComputedBreakdown } from '@/models/settlementTypes';
import { FEE_METHOD, EXPENSE_ARRANGEMENT } from '@/constants/settlementConstants';
import SummaryRow from './SettlementSummaryRow.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'SettlementSummaryCard',
  props: {
  hasSummaryData: { type: Boolean, default: false },
  displayRecoveryAmount: { type: Number, default: 0 },
  feePercentage: { type: Number as () => number | null, default: null },
  breakdown: { type: Object as () => ComputedBreakdown | null, default: null },
  lienTotal: { type: Number, default: 0 },
  completedSteps: { type: Array as () => number[], default: (): number[] => [] },
  feeMethod: { type: String as () => string | null, default: null },
  expenseArrangement: { type: String as () => string | null, default: null },
  /** Mirrors SettlementCalculationService's RoundAmount so the pre-confirmation predicted fee
   *  below lands on the same value the backend will produce — otherwise a firm with this setting
   *  on would see the prediction snap from cents to whole dollars the moment Step 1 is confirmed. */
  roundToNearestDollar: { type: Boolean, default: false },
  isFullyComplete: { type: Boolean, default: false },
  generatedDate: { type: String, default: null },
  /** Gate redesign styles (italic estimates, dark-grey unconfirmed text, estimates footnote).
   *  false = old design (grey-lighten-1 unconfirmed, no footnote). */
  redesignEnabled: { type: Boolean, default: false },
  // SC-45815 — backend-derived confirmed/estimated flags (Is*Estimated inverted by the store's
  // mapApiToFrontend). Pre-population data itself always flows regardless of redesignEnabled;
  // only the italic/bold styling below is gated on it.
  isExpensesConfirmed: { type: Boolean, default: false },
  isLiensConfirmed: { type: Boolean, default: false },
  isAttorneyFeeConfirmed: { type: Boolean, default: false },
  isClientNetRecoveryConfirmed: { type: Boolean, default: false },
},
  setup(__props) {

const props = __props;

// Step-completion shorthand
const hasStep1 = computed(() => props.completedSteps.includes(1));
const hasStep2 = computed(() => props.completedSteps.includes(2));
const hasStep3 = computed(() => props.completedSteps.includes(3));
const hasStep4 = computed(() => props.completedSteps.includes(4));

// Variant helpers
const isNet = computed(() => props.feeMethod === FEE_METHOD.NET);
const isAbsorbed = computed(() => props.expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED);
const isClientPays = computed(() => props.expenseArrangement === EXPENSE_ARRANGEMENT.CLIENT);
const costLabel = computed(() => (isClientPays.value ? 'Case Costs' : 'Expenses Advanced'));

// 0% is a valid configured contingency (pro bono) — must read as "0%", not fall through to the
// unconfigured "—" that null gets below.
const feePercentageLabel = computed(() => (props.feePercentage != null ? `${props.feePercentage}%` : '—'));

// totalExpenses includes expense interest folded in (see backend PersistCalculationSnapshots) —
// subtract it back out here so the "Less: Expenses" line and the standalone "Less: Expense
// Interest" line don't double-count. No-op when there's no interest (most settlements).
// Co-counsel expenses are added in separately — the backend keeps them out of totalExpenses
// and reports them via coCounselExpensesTotal instead (see ApiSettlementSummary).
const netExpenses = computed(() => {
  if (props.breakdown?.totalExpenses == null) return null;
  return (
    props.breakdown.totalExpenses -
    (props.breakdown?.expenseInterest ?? 0) +
    (props.breakdown?.coCounselExpensesTotal ?? 0)
  );
});

// Unlike Expenses/Liens (always a real number — $0.00 when none exist, per the ticket), Attorney
// Fee has no matter-level "unconfirmed" data source: pre-confirmation it's either an existing
// settlement-fee charge (a real number) or nothing at all (blank, not $0.00) — the ticket's own
// wording is "existing settlement fee if one exists; otherwise blank". Applies to `fee` and
// `netAttorneyFee` alike — both stay null until Calculate() has something to derive them from.
function formatFeeValue(value: number | null | undefined): string {
  return value == null ? '—' : formatCurrency(-Math.abs(value));
}

// Pre-Step-1 prediction: mirrors SettlementCalculationService.cs's core formula so Attorney Fee /
// Net Attorney Fee / Client Net Recovery never sit blank just because Recovery Amount hasn't been
// confirmed yet — every input here is already live (selected-deposit total, live expense/lien
// estimates, configured %). Deliberately approximate: skips co-counsel splits (not yet live in
// production) and roundFeeToNearestDollar. Only ever used as a fallback when the backend hasn't
// supplied a real value (breakdown?.fee/clientNet null) — once Recovery Amount is confirmed, the
// backend's authoritative Calculate() result takes over immediately and this computed naturally
// returns null via its own guard clause.
const predictedAttorneyFee = computed(() => {
  if (props.breakdown?.fee != null) return null;
  if (props.feePercentage == null || props.displayRecoveryAmount <= 0) return null;
  const feeBase = props.feeMethod === FEE_METHOD.NET
    ? props.displayRecoveryAmount - (props.breakdown?.totalExpenses ?? 0)
    : props.displayRecoveryAmount;
  // Matches SettlementCalculationService.RoundAmount: whole dollars when the firm has that
  // setting on, 2dp otherwise. (JS Math.round is half-up, same as .NET AwayFromZero for the
  // positive values a fee always takes.) Both branches keep the multiply in one step rather
  // than dividing then re-multiplying, so no float drift creeps into the cents case.
  return props.roundToNearestDollar
    ? Math.round((feeBase * props.feePercentage) / 100)
    : Math.round(feeBase * props.feePercentage) / 100;
});

// Both fee methods get a Net Attorney Fee under Absorbed now (SC-46217/BR-24 fee-reduction fix) —
// the recorded/invoiced amount is always the fee minus the absorbed costs.
const predictedNetAttorneyFee = computed(() => {
  if (props.breakdown?.netAttorneyFee != null || predictedAttorneyFee.value == null) return null;
  if (!isAbsorbed.value) return null;
  return predictedAttorneyFee.value - (props.breakdown?.totalExpenses ?? 0);
});

const predictedClientNetRecovery = computed(() => {
  if (props.breakdown?.clientNet != null) return null;
  const fee = props.breakdown?.fee ?? predictedAttorneyFee.value;
  if (fee == null) return null;
  const expenses = props.breakdown?.totalExpenses ?? 0;
  const liens = props.lienTotal ?? 0;
  // Mirrors SettlementCalculationService: since SC-46217/BR-24, both Absorbed arrangements share
  // the same shape — the firm's reduced take-home (netAttorneyFee) never affects the client, so
  // there is no separate expense term: recovery − fee − liens. Everything else (Firm/Client
  // advances) — the client bears the costs directly: recovery − fee − expenses − liens.
  if (isAbsorbed.value) {
    return props.displayRecoveryAmount - fee - liens;
  }
  return props.displayRecoveryAmount - fee - expenses - liens;
});

const displayedFee = computed(() => props.breakdown?.fee ?? predictedAttorneyFee.value);
const displayedNetAttorneyFee = computed(() => props.breakdown?.netAttorneyFee ?? predictedNetAttorneyFee.value);
const displayedClientNet = computed(() => props.breakdown?.clientNet ?? predictedClientNetRecovery.value);

/**
 * Redesign: mutually-exclusive bold/black (confirmed) vs italic/gray (estimate) per SC-45815.
 * Legacy: unchanged from pre-SC-45815 behavior — always bold, only the color dims when unconfirmed.
 */
function emphasisClass(confirmed: boolean, legacyHasStep: boolean): string {
  if (props.redesignEnabled) {
    return confirmed ? 'font-weight-bold' : 'font-italic text-grey-darken-1';
  }
  return legacyHasStep ? 'font-weight-bold' : 'font-weight-bold text-grey-lighten-1';
}

// Must NOT fire on a merely-unavailable value (displayedClientNet null — e.g. no contingency %
// configured yet, and not enough live inputs to predict from either): that's "nothing to show"
// (blank, neutral), not "a bad outcome" — falling back to `?? 0` here would show the negative/red
// warning tooltip for data that was simply never computed or predicted.
const isClientNetRecoveryNegativeOrZero = computed(
  () => displayedClientNet.value != null && displayedClientNet.value <= 0,
);

const clientNetRecoveryClass = computed(() => {
  if (props.isClientNetRecoveryConfirmed) {
    return (displayedClientNet.value ?? 0) < 0
      ? 'font-weight-bold text-error'
      : 'font-weight-bold text-green-darken-2';
  }
  return isClientNetRecoveryNegativeOrZero.value
    ? 'font-italic text-error'
    : 'font-italic text-grey-darken-1';
});

// Generated date formatter
const generatedDateFormatted = computed(() => {
  if (!props.generatedDate) return '';
  return formatLongDateString(props.generatedDate) || props.generatedDate;
});

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createBlock(_component_v_card, {
    variant: "outlined",
    elevation: "0",
    class: "sett-summary-card !bg-[rgb(var(--v-theme-surface))]"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_card_title, {
        class: "text-subtitle-1 text-center",
        style: {"padding":"14px 18px","border-bottom":"1px solid #eee"}
      }, {
        default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
          _createTextVNode(" Settlement Summary ", -1)
        ]))]),
        _: 1
      }),
      _createVNode(_component_v_card_text, { style: {"padding":"16px 18px"} }, {
        default: _withCtx(() => [
          (__props.hasSummaryData)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(SummaryRow, { label: "Recovery Amount" }, {
                  badge: _withCtx(() => [
                    (hasStep1.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
                          _createVNode(LlIcon, {
                            icon: __props.isFullyComplete ? 'mdi-lock' : 'mdi-check',
                            size: "12",
                            color: __props.isFullyComplete ? 'grey-darken-1' : 'success'
                          }, null, 8, ["icon", "color"])
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  value: _withCtx(() => [
                    _createElementVNode("span", _hoisted_2, _toDisplayString(__props.displayRecoveryAmount > 0 ? _unref(formatCurrency)(__props.displayRecoveryAmount) : '—'), 1)
                  ]),
                  _: 1
                }),
                (isAbsorbed.value && isNet.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("div", _hoisted_3, [
                        _createElementVNode("span", _hoisted_4, "Less: " + _toDisplayString(costLabel.value) + " (absorbed)", 1),
                        _createElementVNode("span", {
                          class: _normalizeClass(["text-caption text-grey-darken-1", __props.redesignEnabled ? { 'font-italic': !__props.isExpensesConfirmed } : { 'text-grey-lighten-1': !__props.isExpensesConfirmed }])
                        }, _toDisplayString(_unref(formatCurrency)(netExpenses.value ?? 0)), 3)
                      ]),
                      (hasStep2.value && __props.breakdown?.adjustedRecovery != null)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            _cache[1] || (_cache[1] = _createElementVNode("span", { class: "text-body-2 font-weight-bold" }, "Net for Fee Calc", -1)),
                            _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(formatCurrency)(__props.breakdown.adjustedRecovery)), 1)
                          ]))
                        : _createCommentVNode("", true),
                      _createVNode(SummaryRow, {
                        label: `Attorney Fee (${feePercentageLabel.value})`
                      }, {
                        badge: _withCtx(() => [
                          (__props.isAttorneyFeeConfirmed)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_7, [
                                _createVNode(LlIcon, {
                                  icon: __props.isFullyComplete ? 'mdi-lock' : 'mdi-check',
                                  size: "12",
                                  color: __props.isFullyComplete ? 'grey-darken-1' : 'success'
                                }, null, 8, ["icon", "color"])
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        value: _withCtx(() => [
                          _createElementVNode("span", {
                            class: _normalizeClass(["text-body-2", emphasisClass(__props.isAttorneyFeeConfirmed, hasStep3.value)])
                          }, _toDisplayString(formatFeeValue(displayedFee.value)), 3)
                        ]),
                        _: 1
                      }, 8, ["label"]),
                      _createElementVNode("div", _hoisted_8, [
                        _cache[2] || (_cache[2] = _createElementVNode("span", { class: "text-caption text-grey-darken-1" }, "Net Attorney Fee", -1)),
                        _createElementVNode("span", {
                          class: _normalizeClass(["text-caption text-grey-darken-1", __props.redesignEnabled ? { 'font-italic': !__props.isAttorneyFeeConfirmed } : { 'text-grey-lighten-1': !__props.isAttorneyFeeConfirmed }])
                        }, _toDisplayString(displayedNetAttorneyFee.value != null ? _unref(formatCurrency)(displayedNetAttorneyFee.value) : '—'), 3)
                      ])
                    ], 64))
                  : (isAbsorbed.value && !isNet.value)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createVNode(SummaryRow, {
                          label: `Gross Fee (${feePercentageLabel.value})`
                        }, {
                          value: _withCtx(() => [
                            _createElementVNode("span", {
                              class: _normalizeClass(["text-body-2", emphasisClass(__props.isAttorneyFeeConfirmed, hasStep3.value)])
                            }, _toDisplayString(formatFeeValue(displayedFee.value)), 3)
                          ]),
                          _: 1
                        }, 8, ["label"]),
                        _createElementVNode("div", _hoisted_9, [
                          _createElementVNode("span", _hoisted_10, "Less: " + _toDisplayString(costLabel.value) + " (absorbed)", 1),
                          _createElementVNode("span", {
                            class: _normalizeClass(["text-caption text-grey-darken-1", __props.redesignEnabled ? { 'font-italic': !__props.isExpensesConfirmed } : { 'text-grey-lighten-1': !__props.isExpensesConfirmed }])
                          }, _toDisplayString(_unref(formatCurrency)(netExpenses.value ?? 0)), 3)
                        ]),
                        _createElementVNode("div", _hoisted_11, [
                          _cache[3] || (_cache[3] = _createElementVNode("span", { class: "text-caption text-grey-darken-1" }, "Net Attorney Fee", -1)),
                          _createElementVNode("span", {
                            class: _normalizeClass(["text-caption text-grey-darken-1", __props.redesignEnabled ? { 'font-italic': !__props.isAttorneyFeeConfirmed } : { 'text-grey-lighten-1': !__props.isAttorneyFeeConfirmed }])
                          }, _toDisplayString(displayedNetAttorneyFee.value != null ? _unref(formatCurrency)(displayedNetAttorneyFee.value) : '—'), 3)
                        ])
                      ], 64))
                    : (isNet.value)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                          _createVNode(SummaryRow, {
                            label: `Less: ${costLabel.value}`
                          }, {
                            badge: _withCtx(() => [
                              (__props.isExpensesConfirmed)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_12, [
                                    _createVNode(LlIcon, {
                                      icon: __props.isFullyComplete ? 'mdi-lock' : 'mdi-check',
                                      size: "12",
                                      color: __props.isFullyComplete ? 'grey-darken-1' : 'success'
                                    }, null, 8, ["icon", "color"])
                                  ]))
                                : _createCommentVNode("", true)
                            ]),
                            value: _withCtx(() => [
                              _createElementVNode("span", {
                                class: _normalizeClass(["text-body-2", emphasisClass(__props.isExpensesConfirmed, hasStep2.value)])
                              }, _toDisplayString(_unref(formatCurrency)(-Math.abs(netExpenses.value ?? 0))), 3)
                            ]),
                            _: 1
                          }, 8, ["label"]),
                          (hasStep2.value && isClientPays.value && (__props.breakdown?.alreadyPaidByClient ?? 0) > 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                _cache[4] || (_cache[4] = _createElementVNode("span", { class: "text-caption text-grey-darken-1" }, "Already paid by client", -1)),
                                _createElementVNode("span", _hoisted_14, _toDisplayString(_unref(formatCurrency)(__props.breakdown.alreadyPaidByClient)), 1)
                              ]))
                            : _createCommentVNode("", true),
                          (hasStep2.value && (__props.breakdown?.expenseInterest ?? 0) > 0)
                            ? (_openBlock(), _createBlock(SummaryRow, {
                                key: 1,
                                label: "Less: Expense Interest"
                              }, {
                                value: _withCtx(() => [
                                  _createElementVNode("span", _hoisted_15, _toDisplayString(_unref(formatCurrency)(-__props.breakdown.expenseInterest)), 1)
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          (hasStep2.value && __props.breakdown?.adjustedRecovery != null)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-body-2 font-weight-bold" }, "Net for Fee Calc", -1)),
                                _createElementVNode("span", _hoisted_17, _toDisplayString(_unref(formatCurrency)(__props.breakdown.adjustedRecovery)), 1)
                              ]))
                            : _createCommentVNode("", true),
                          _createVNode(SummaryRow, {
                            label: `Attorney Fee (${feePercentageLabel.value})`
                          }, {
                            badge: _withCtx(() => [
                              (__props.isAttorneyFeeConfirmed)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_18, [
                                    _createVNode(LlIcon, {
                                      icon: __props.isFullyComplete ? 'mdi-lock' : 'mdi-check',
                                      size: "12",
                                      color: __props.isFullyComplete ? 'grey-darken-1' : 'success'
                                    }, null, 8, ["icon", "color"])
                                  ]))
                                : _createCommentVNode("", true)
                            ]),
                            value: _withCtx(() => [
                              _createElementVNode("span", {
                                class: _normalizeClass(["text-body-2", emphasisClass(__props.isAttorneyFeeConfirmed, hasStep3.value)])
                              }, _toDisplayString(formatFeeValue(displayedFee.value)), 3)
                            ]),
                            _: 1
                          }, 8, ["label"])
                        ], 64))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                          _createVNode(SummaryRow, {
                            label: `Attorney Fee (${feePercentageLabel.value})`
                          }, {
                            badge: _withCtx(() => [
                              (__props.isAttorneyFeeConfirmed)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_19, [
                                    _createVNode(LlIcon, {
                                      icon: __props.isFullyComplete ? 'mdi-lock' : 'mdi-check',
                                      size: "12",
                                      color: __props.isFullyComplete ? 'grey-darken-1' : 'success'
                                    }, null, 8, ["icon", "color"])
                                  ]))
                                : _createCommentVNode("", true)
                            ]),
                            value: _withCtx(() => [
                              _createElementVNode("span", {
                                class: _normalizeClass(["text-body-2", emphasisClass(__props.isAttorneyFeeConfirmed, hasStep3.value)])
                              }, _toDisplayString(formatFeeValue(displayedFee.value)), 3)
                            ]),
                            _: 1
                          }, 8, ["label"]),
                          _createVNode(SummaryRow, {
                            label: `Less: ${costLabel.value}`
                          }, {
                            badge: _withCtx(() => [
                              (__props.isExpensesConfirmed)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_20, [
                                    _createVNode(LlIcon, {
                                      icon: __props.isFullyComplete ? 'mdi-lock' : 'mdi-check',
                                      size: "12",
                                      color: __props.isFullyComplete ? 'grey-darken-1' : 'success'
                                    }, null, 8, ["icon", "color"])
                                  ]))
                                : _createCommentVNode("", true)
                            ]),
                            value: _withCtx(() => [
                              _createElementVNode("span", {
                                class: _normalizeClass(["text-body-2", emphasisClass(__props.isExpensesConfirmed, hasStep2.value)])
                              }, _toDisplayString(_unref(formatCurrency)(-Math.abs(netExpenses.value ?? 0))), 3)
                            ]),
                            _: 1
                          }, 8, ["label"]),
                          (hasStep2.value && isClientPays.value && (__props.breakdown?.alreadyPaidByClient ?? 0) > 0)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                                _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-caption text-grey-darken-1" }, "Already paid by client", -1)),
                                _createElementVNode("span", _hoisted_22, _toDisplayString(_unref(formatCurrency)(__props.breakdown.alreadyPaidByClient)), 1)
                              ]))
                            : _createCommentVNode("", true)
                        ], 64)),
                (hasStep2.value && !isNet.value && (__props.breakdown?.expenseInterest ?? 0) > 0)
                  ? (_openBlock(), _createBlock(SummaryRow, {
                      key: 4,
                      label: "Less: Expense Interest"
                    }, {
                      value: _withCtx(() => [
                        _createElementVNode("span", _hoisted_23, _toDisplayString(_unref(formatCurrency)(-__props.breakdown.expenseInterest)), 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                ((__props.breakdown?.salesTax ?? 0) > 0)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.breakdown?.taxComponents ?? [], (component) => {
                        return (_openBlock(), _createBlock(SummaryRow, {
                          key: component.name,
                          label: `Less: ${component.name}`
                        }, {
                          value: _withCtx(() => [
                            _createElementVNode("span", _hoisted_24, _toDisplayString(_unref(formatCurrency)(-component.amount)), 1)
                          ]),
                          _: 2
                        }, 1032, ["label"]))
                      }), 128)),
                      (!(__props.breakdown?.taxComponents ?? []).length)
                        ? (_openBlock(), _createBlock(SummaryRow, {
                            key: 0,
                            label: "Less: Sales Tax"
                          }, {
                            value: _withCtx(() => [
                              _createElementVNode("span", _hoisted_25, _toDisplayString(_unref(formatCurrency)(-__props.breakdown.salesTax)), 1)
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true)
                    ], 64))
                  : _createCommentVNode("", true),
                _createVNode(SummaryRow, { label: "Less: Third-Party Liens" }, {
                  badge: _withCtx(() => [
                    (__props.isLiensConfirmed)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_26, [
                          _createVNode(LlIcon, {
                            icon: __props.isFullyComplete ? 'mdi-lock' : 'mdi-check',
                            size: "12",
                            color: __props.isFullyComplete ? 'grey-darken-1' : 'success'
                          }, null, 8, ["icon", "color"])
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  value: _withCtx(() => [
                    _createElementVNode("span", {
                      class: _normalizeClass(["text-body-2", emphasisClass(__props.isLiensConfirmed, hasStep4.value)])
                    }, _toDisplayString(_unref(formatCurrency)(-(__props.lienTotal ?? 0))), 3)
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_divider, { class: "my-2" }),
                _createElementVNode("div", _hoisted_27, [
                  _cache[7] || (_cache[7] = _createElementVNode("span", { class: "text-body-2 font-weight-bold text-grey-darken-2" }, " Client Net Recovery ", -1)),
                  _createElementVNode("span", _hoisted_28, [
                    _createElementVNode("span", {
                      class: _normalizeClass(["text-body-1", clientNetRecoveryClass.value])
                    }, _toDisplayString(displayedClientNet.value != null ? _unref(formatCurrency)(displayedClientNet.value) : '—'), 3),
                    (!__props.isClientNetRecoveryConfirmed && isClientNetRecoveryNegativeOrZero.value)
                      ? (_openBlock(), _createBlock(LlTooltip, {
                          key: 0,
                          icon: "warning",
                          text: "Client net recovery is zero or negative — review expenses and liens before proceeding."
                        }))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _createVNode(_component_v_divider, { class: "my-2" }),
                (generatedDateFormatted.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
                      _cache[8] || (_cache[8] = _createElementVNode("span", { class: "text-body-2 text-grey-darken-1" }, "Statement", -1)),
                      _createElementVNode("div", _hoisted_30, [
                        _createVNode(LlIcon, {
                          icon: "mdi-check",
                          size: "14",
                          color: "success",
                          class: "mr-1"
                        }),
                        _createTextVNode(" Generated " + _toDisplayString(generatedDateFormatted.value), 1)
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ], 64))
            : (_openBlock(), _createElementBlock("div", _hoisted_31, " Complete each step to build your settlement breakdown. "))
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})